import { useEffect, useRef, useState, useCallback, useId } from 'react';
import PropTypes from 'prop-types';

function ModernSelect({ value, onChange, options, placeholder = 'Select...', onOpen, onFocus, disabled = false, error = false }) {
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);
  // Removed search bar logic
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  // Removed scroll shadow decoration to match college search UI
  const listId = useId();

  const selected = options?.find(o => o.value === value) || null;

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => {
    setOpen(o => {
      const next = !o;
      if (next) {
        if (onOpen) onOpen();
        if (onFocus) onFocus();
      }
      return next;
    });
  }, [onOpen, onFocus]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        close();
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, close]);

  const commit = (opt) => {
    if (!opt) return;
    if (onFocus) onFocus();
    onChange?.(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
      e.preventDefault();
      setOpen(true);
      onOpen?.();
      setHoverIdx(Math.max(0, options.findIndex(o => o.value === value)));
      return;
    }
    if (!open) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoverIdx(i => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoverIdx(i => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Escape') {
      close();
    } else if (e.key === 'Enter') {
      if (hoverIdx >= 0 && hoverIdx < options.length) {
        commit(options[hoverIdx]);
      }
    }
  };

  // Removed GSAP animation; relying on simple CSS instead (no animation on open to keep it lightweight).

  // No scroll shadow logic needed for the simplified dropdown UI

  return (
    <div
      ref={rootRef}
      className={`relative ${disabled ? 'opacity-60 pointer-events-none' : ''}`}
      onKeyDown={onKeyDown}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-controls={open ? listId : undefined}
    >
      <button
        type="button"
        onClick={toggle}
        onFocus={onFocus}
        className={`w-full px-3.5 py-2 md:py-2.5 text-left rounded-none border text-white transition-all focus:outline-none ${
          error ? 'border-rose-400/60 ring-2 ring-rose-400/60' : 'border-white/15 focus:ring-2 focus:ring-sky-400'
        } bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-lg shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]`}
        aria-controls={open ? listId : undefined}
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 pr-5">
          <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-sky-400 to-indigo-400 shadow-[0_0_12px_rgba(59,130,246,0.5)]" />
          <span className={`${selected ? 'text-white' : 'text-white/60'}`}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <svg className={`absolute w-4 h-4 right-3 top-1/2 -translate-y-1/2 text-white/70 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div
          ref={menuRef}
          className="absolute left-0 right-0 z-50 mt-2 top-full overflow-hidden border rounded-md border-white/10 backdrop-blur-sm"
          style={{ backgroundColor: 'rgba(5, 56, 89, 0.6)' }}
        >
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            className="py-1 overflow-auto text-sm max-h-48 md:max-h-60 md:text-base"
          >
            {options.length === 0 ? (
              <li className="px-3 py-2 text-sm text-white/60">No results found</li>
            ) : (
              options.map((opt, i) => {
                const active = i === hoverIdx || opt.value === value;
                return (
                  <li key={opt.value} role="option" aria-selected={opt.value === value}>
                    <button
                      type="button"
                      className={`w-full px-3 py-2 text-left text-white transition-colors md:px-4 md:py-3 ${active ? 'bg-sky-500/20' : 'hover:bg-sky-500/20'}`}
                      onMouseEnter={() => setHoverIdx(i)}
                      onClick={() => commit(opt)}
                    >
                      {opt.label}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

ModernSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired })).isRequired,
  placeholder: PropTypes.string,
  onOpen: PropTypes.func,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
};

export default ModernSelect;
